import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ThemeProvider,
  useTheme,
  alpha,
  responsiveFontSizes,
} from '@mui/material'
import Bugsnag from '@bugsnag/js'
import { AxiosError } from 'axios'
import { client } from '../utils/client'
import { RegistrationGroupItemType, RegistrationGroupType } from '../utils/sharedTypes'
import { CallUs } from '../components'
import { ReactComponent as WalterLearning } from '../assets/WalterLearning.svg'
import { ReactComponent as WalterSanteColor } from '../assets/WalterSanteColor.svg'
import { ReactComponent as LogoEdumiam } from '../assets/edumiam.svg'
import CPFLogo from '../assets/CPFLogo.png'
import { productLogotypeBlackUrl } from '../components/marketplace/marketplaceAssets'
import { learningPhoneNumber, santePhoneNumber } from '../utils/constants'
import { EdumiamTheme, WLtheme, WStheme } from '../utils/theme'
import { isEdumiamHostname } from '../utils/featureFlags'
import { BsCheck } from 'react-icons/bs'
import { MdSchool, MdLocalHospital } from 'react-icons/md'

// Cf. `status_badge_info` (walter-crm, registration.py) pour la liste des variantes possibles.
const CHIP_COLOR_BY_VARIANT: Record<string, 'success' | 'error' | 'warning' | 'info' | 'secondary' | 'default'> = {
  green: 'success',
  red: 'error',
  amber: 'warning',
  blue: 'info',
  teal: 'info',
  purple: 'secondary',
  gray: 'default',
  outline: 'default',
  default: 'default',
}

// Ordre de préférence : le logotype de la formation (visuels S3 publics, déjà utilisés par le
// funnel), puis le logo CPF, puis une icône générique. Tous les produits n'ont pas de logotype
// généré, d'où le repli sur échec de chargement.
const RegistrationLogo = ({ item, chipColor }: { item: RegistrationGroupItemType; chipColor: string }) => {
  const theme = useTheme()
  const [logotypeFailed, setLogotypeFailed] = useState(false)
  const productCode = item.products[0]?.product_code || item.product?.product_code

  if (productCode && !logotypeFailed) {
    // Ces logotypes sont des signatures larges : dans un carré de 56 px ils deviennent illisibles,
    // d'où un conteneur en format paysage.
    return (
      <Avatar
        variant="rounded"
        src={productLogotypeBlackUrl(productCode)}
        alt=""
        imgProps={{ style: { objectFit: 'contain', padding: 8 }, onError: () => setLogotypeFailed(true) }}
        sx={{ width: 104, height: 56, bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
      />
    )
  }

  if (item.funding_organism?.code === 'CPF') {
    return (
      <Avatar
        variant="rounded"
        src={CPFLogo}
        alt="Logo CPF"
        imgProps={{ style: { objectFit: 'contain', padding: 6 } }}
        sx={{ width: 56, height: 56, bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}
      />
    )
  }

  const iconColor: string =
    chipColor === 'success'
      ? theme.palette.success.main
      : chipColor === 'error'
      ? theme.palette.error.main
      : chipColor === 'warning'
      ? theme.palette.warning.main
      : chipColor === 'info'
      ? theme.palette.info.main
      : chipColor === 'secondary'
      ? theme.palette.secondary.main
      : theme.palette.grey[600]
  const bgColor = chipColor === 'default' ? theme.palette.grey[200] : alpha(iconColor, 0.12)

  return (
    <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: bgColor, color: iconColor }}>
      {item.theme === 'sante' ? <MdLocalHospital size={26} /> : <MdSchool size={26} />}
    </Avatar>
  )
}

// Une inscription validée n'a pas toujours immédiatement un compte LMS actif en face (création
// asynchrone côté `app.walter-learning.com`) : on retente quelques fois avant d'abandonner
// silencieusement (le bouton reste alors simplement absent).
const MAGIC_LINK_MAX_RETRY = 3
const MAGIC_LINK_RETRY_DELAY_MS = 4000

const useMagicLink = (globalId: string, validated: boolean): string | null => {
  const [magicLink, setMagicLink] = useState<string | null>(null)
  const retryCount = useRef(0)

  useEffect(() => {
    if (!validated) return
    let cancelled = false
    retryCount.current = 0

    const fetchMagicLink = () => {
      client
        .get(`/registration/${globalId}/magic_link/`)
        .then((resp) => {
          if (cancelled) return
          const data = resp.data as { magic_link: string | null }
          if (data.magic_link && data.magic_link !== 'pending') {
            setMagicLink(data.magic_link)
          } else if (data.magic_link === 'pending' && retryCount.current < MAGIC_LINK_MAX_RETRY) {
            retryCount.current += 1
            setTimeout(() => {
              if (!cancelled) fetchMagicLink()
            }, MAGIC_LINK_RETRY_DELAY_MS)
          }
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
    }

    fetchMagicLink()
    return () => {
      cancelled = true
    }
  }, [globalId, validated])

  return magicLink
}

const itemNames = (item: RegistrationGroupItemType): string => {
  const names = item.products.length > 0 ? item.products.map((p) => p.name) : item.product ? [item.product.name] : []
  return names.length > 0 ? names.join(', ') : 'Formation'
}

const itemFunnelPath = (item: RegistrationGroupItemType): string =>
  item.funnel_path || `${item.theme === 'sante' ? '/sante' : ''}/information?global_id=${item.global_id}`

const StatusChips = ({ item }: { item: RegistrationGroupItemType }) => {
  const chipColor = CHIP_COLOR_BY_VARIANT[item.status_badge_info.variant] || 'default'
  return (
    <Grid container spacing={1} sx={{ mt: 0.5 }} alignItems="center">
      {item.funding_organism?.code && (
        <Grid item>
          <Chip size="small" variant="outlined" label={item.funding_organism.code} />
        </Grid>
      )}
      <Grid item>
        <Chip
          size="small"
          variant="outlined"
          color={chipColor}
          label={item.status_badge_info.label}
          title={item.status_badge_info.tooltip || undefined}
        />
      </Grid>
    </Grid>
  )
}

// Une seule action principale par page : l'inscription que le serveur désigne (`next_registration_id`, la
// première qui attend la personne). Les autres se complètent ensuite, sans bouton « Continuer » concurrent.
const NextStepCard = ({ item, remaining }: { item: RegistrationGroupItemType; remaining: number }) => {
  const theme = useTheme()
  const chipColor = CHIP_COLOR_BY_VARIANT[item.status_badge_info.variant] || 'default'
  return (
    <Card
      variant="outlined"
      sx={{
        width: '100%',
        mb: 4,
        borderColor: 'primary.main',
        borderWidth: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>
          Votre prochaine étape
        </Typography>
        <Grid container alignItems="center" spacing={2} sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <Grid item sx={{ display: { xs: 'none', sm: 'block' } }}>
            <RegistrationLogo item={item} chipColor={chipColor} />
          </Grid>
          <Grid item xs={12} sm sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {itemNames(item)}
            </Typography>
            <StatusChips item={item} />
          </Grid>
        </Grid>
        {/* Libellés sur une ligne : sur téléphone, le libellé complet ne tient pas (iPhone SE), la carte nomme déjà
            la formation, « Je continue » suffit. */}
        <Grid container alignItems="center" spacing={1} sx={{ mt: 1 }}>
          <Grid item xs={12} sm="auto">
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to={itemFunnelPath(item)}
              sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 48, whiteSpace: 'nowrap' }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Je continue avec cette inscription
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Je continue
              </Box>
            </Button>
          </Grid>
        </Grid>
        {remaining > 0 && (
          <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
            {remaining === 1
              ? 'Vous compléterez ensuite votre autre formation : vos informations seront déjà remplies.'
              : `Vous compléterez ensuite vos ${remaining} autres formations : vos informations seront déjà remplies.`}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// `onWithdraw` n'est passé qu'en mode « Modifier mes formations » : la carte n'offre alors que le retrait.
const RegistrationItemCard = ({
  item,
  onWithdraw,
}: {
  item: RegistrationGroupItemType
  onWithdraw?: (item: RegistrationGroupItemType) => void
}) => {
  const theme = useTheme()
  const chipColor = CHIP_COLOR_BY_VARIANT[item.status_badge_info.variant] || 'default'
  const magicLink = useMagicLink(item.global_id, item.validated)

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        {/* Retour à la ligne sur téléphone : en `nowrap`, la carte prenait la largeur du titre plus du bouton
            et la page débordait de l'écran. */}
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
        >
          <Grid item sx={{ display: { xs: 'none', sm: 'block' } }}>
            <RegistrationLogo item={item} chipColor={chipColor} />
          </Grid>
          <Grid item xs={12} sm sx={{ minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {itemNames(item)}
            </Typography>
            <StatusChips item={item} />
          </Grid>
          {onWithdraw
            ? item.can_withdraw && (
                <Grid item xs={12} sm="auto">
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => onWithdraw(item)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Retirer
                  </Button>
                </Grid>
              )
            : (item.validated || item.needs_learner_action) && (
                <Grid item xs={12} sm="auto">
                  {item.validated ? (
                    <Grid container direction="column" alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1}>
                      <Grid item container alignItems="center" spacing={1} wrap="nowrap" sx={{ width: 'auto' }}>
                        <Grid item sx={{ display: 'flex', color: theme.palette.success.main }}>
                          <BsCheck size="24px" />
                        </Grid>
                        <Grid item>
                          <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
                            Inscription validée
                          </Typography>
                        </Grid>
                      </Grid>
                      {magicLink && (
                        <Grid item>
                          <Button
                            size="small"
                            variant="contained"
                            component="a"
                            href={magicLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Accéder à la formation
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Grid
                      container
                      wrap="nowrap"
                      alignItems="center"
                      justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                      spacing={1}
                    >
                      {item.needs_learner_action && (
                        <Grid item>
                          <Button
                            size="small"
                            variant="outlined"
                            component={RouterLink}
                            to={itemFunnelPath(item)}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Compléter
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Grid>
              )}
        </Grid>
      </CardContent>
    </Card>
  )
}

const RegistrationGroupDashboard = () => {
  const { group_global_id } = useParams<{ group_global_id: string }>()
  const navigate = useNavigate()
  const [group, setGroup] = useState<RegistrationGroupType | null>(null)
  // "not_found" : le groupe n'existe vraiment pas (404, état définitif).
  // "network" : erreur transitoire (backend indisponible, coupure réseau...), on propose de réessayer.
  const [error, setError] = useState<'not_found' | 'network' | null>(null)
  // « Modifier mes formations » : le retrait vit dans ce mode, pas à côté du parcours.
  const [managing, setManaging] = useState(false)
  const [withdrawing, setWithdrawing] = useState<RegistrationGroupItemType | null>(null)
  const [withdrawPending, setWithdrawPending] = useState(false)
  const [withdrawError, setWithdrawError] = useState<string | null>(null)

  const openWithdraw = (item: RegistrationGroupItemType) => {
    // Une erreur d'un essai précédent ne doit pas s'afficher pour cette formation-ci.
    setWithdrawError(null)
    setWithdrawing(item)
  }

  const confirmWithdraw = () => {
    if (!group || !withdrawing) return
    setWithdrawPending(true)
    setWithdrawError(null)
    client
      .post(`/registration/registration_group/${group.global_id}/withdraw/`, { registration: withdrawing.global_id })
      .then((resp) => {
        const updated = resp.data as RegistrationGroupType
        setGroup(updated)
        setWithdrawing(null)
        if (!updated.registrations.some((r) => r.can_withdraw)) setManaging(false)
      })
      .catch((err: AxiosError<{ detail?: string }>) => {
        setWithdrawError(err.response?.data?.detail || 'Le retrait a échoué, réessayez dans un instant.')
        if (!err.response || err.response.status >= 500) Bugsnag.notify(err)
      })
      .finally(() => setWithdrawPending(false))
  }

  const fetchGroup = useCallback(() => {
    if (!group_global_id) return
    setError(null)
    client
      .get(`/registration/registration_group/${group_global_id}/`)
      .then((resp) => {
        setGroup(resp.data as RegistrationGroupType)
      })
      .catch((err: AxiosError) => {
        if (err.response?.status === 404) {
          setError('not_found')
        } else {
          setError('network')
          Bugsnag.notify(err)
          console.error(err)
        }
      })
  }, [group_global_id])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    fetchGroup()
  }, [fetchGroup])

  // Un lot IPERIA autonome (tous ses dossiers suivent le tunnel IPERIA) et le domaine Edumiam
  // s'habillent en Edumiam : la personne ne doit pas croiser la marque Walter.
  const edumiam =
    isEdumiamHostname() ||
    Boolean(group && group.registrations.length > 0 && group.registrations.every((r) => r.funnel_path))
  const displayTheme: 'sante' | 'learning' | 'edumiam' = edumiam
    ? 'edumiam'
    : group?.registrations.some((r) => r.theme === 'sante')
    ? 'sante'
    : 'learning'
  // Les routes `/groupe/...` sont hors du pattern `/sante?/*` géré par App.tsx : sans ce
  // ThemeProvider imbriqué, un hub santé s'afficherait avec le thème noir Learning.
  const muiTheme = useMemo(
    () => responsiveFontSizes(displayTheme === 'edumiam' ? EdumiamTheme : displayTheme === 'sante' ? WStheme : WLtheme),
    [displayTheme],
  )

  const nextItem = group?.registrations.find((r) => r.global_id === group.next_registration_id) ?? null
  // La prochaine étape est en tête de page : la liste ne la répète pas.
  const otherItems = group?.registrations.filter((r) => r !== nextItem) ?? []

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog open={withdrawing !== null} onClose={() => !withdrawPending && setWithdrawing(null)}>
        <DialogTitle>Retirer cette formation ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {withdrawing ? `« ${itemNames(withdrawing)} » ne fera plus partie de vos inscriptions.` : ''} Vous pourrez
            l'ajouter à nouveau plus tard.
          </DialogContentText>
          {withdrawError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {withdrawError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawing(null)} disabled={withdrawPending}>
            Annuler
          </Button>
          <Button color="error" variant="contained" onClick={confirmWithdraw} disabled={withdrawPending}>
            Retirer
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container justifyContent="center" sx={{ minHeight: '100vh', p: 2 }}>
        <Grid
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            pt: 2,
            pl: { xs: 2, md: 4 },
          }}
        >
          {displayTheme === 'edumiam' ? (
            <LogoEdumiam width={180} height={48} style={{ display: 'block', overflow: 'visible' }} />
          ) : displayTheme === 'sante' ? (
            <WalterSanteColor width="162px" />
          ) : (
            <WalterLearning width="180px" />
          )}
        </Grid>

        <Grid
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            mt: 2,
            mr: 4,
            display: { xs: 'none', md: 'block' },
          }}
        >
          {/* Edumiam reprend le numéro de Walter Learning tant qu'il n'a pas le sien. */}
          <CallUs phone={displayTheme === 'sante' ? santePhoneNumber : learningPhoneNumber} />
        </Grid>

        <Grid item xs={12} md={8} sx={{ mt: { xs: 15, md: 12 }, mb: 6 }}>
          {error === 'not_found' && (
            <Grid container justifyContent="center">
              <Typography variant="h5" align="center">
                Ce lien n'est plus valide.
              </Typography>
            </Grid>
          )}

          {error === 'network' && (
            <Grid container direction="column" alignItems="center" spacing={2}>
              <Grid item>
                <Typography variant="h6" align="center">
                  Une erreur est survenue pendant le chargement de vos inscriptions.
                </Typography>
              </Grid>
              <Grid item>
                <Button variant="contained" onClick={fetchGroup}>
                  Réessayer
                </Button>
              </Grid>
            </Grid>
          )}

          {!error && !group && (
            <Grid container justifyContent="center">
              <CircularProgress />
            </Grid>
          )}

          {!error && group && (
            <>
              <Typography variant="h4" sx={{ mb: 1 }}>
                {group.first_name ? `Bonjour ${group.first_name},` : 'Bonjour,'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Retrouvez ici l'ensemble de vos inscriptions et leur avancement.
              </Typography>

              {managing ? (
                <>
                  <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Grid item>
                      <Typography variant="h6">Modifier mes formations</Typography>
                    </Grid>
                    <Grid item>
                      <Button variant="contained" onClick={() => setManaging(false)}>
                        Terminé
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={2}>
                    {group.registrations.map((item) => (
                      <Grid item key={item.global_id}>
                        <RegistrationItemCard item={item} onWithdraw={openWithdraw} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : (
                <>
                  {nextItem ? (
                    <NextStepCard item={nextItem} remaining={otherItems.filter((r) => r.needs_learner_action).length} />
                  ) : (
                    group.registrations.some((r) => !r.validated) && (
                      <Typography variant="body1" sx={{ mb: 4 }}>
                        Rien à faire de votre côté pour le moment : nous vous prévenons dès qu'une étape vous attend.
                      </Typography>
                    )
                  )}

                  {otherItems.length > 0 && (
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {nextItem ? 'Vos autres formations' : 'Vos formations'}
                    </Typography>
                  )}
                  <Grid container direction="column" spacing={2}>
                    {otherItems.map((item) => (
                      <Grid item key={item.global_id}>
                        <RegistrationItemCard item={item} />
                      </Grid>
                    ))}
                    {group.registrations.length === 0 && (
                      <Grid item>
                        <Typography variant="body2" align="center" sx={{ color: 'text.disabled' }}>
                          Aucune inscription active pour le moment.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* Gérer le panier (ajouter, retirer) se fait en bas, loin du parcours. */}
                  <Grid container direction="column" alignItems="center" spacing={1} sx={{ mt: 4 }}>
                    {group.self_registration_only && (
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            navigate(`/groupe/${group.global_id}/marketplace`)
                          }}
                        >
                          Ajouter une formation
                        </Button>
                      </Grid>
                    )}
                    {group.registrations.some((r) => r.can_withdraw) && (
                      <Grid item>
                        <Button
                          size="small"
                          color="inherit"
                          sx={{ color: 'text.secondary' }}
                          onClick={() => setManaging(true)}
                        >
                          Modifier mes formations
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </ThemeProvider>
  )
}

export default RegistrationGroupDashboard
